import * as React from "react";
import { useTranslation } from "react-i18next";
import { FileArchive, HardDriveUpload, Loader2, Settings2, Sparkles, UploadCloud, X } from "lucide-react";
import { type Lang } from "@/app/i18n/types";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { Textarea } from "@/app/components/ui/textarea";
import { Label, RequiredMark } from "@/app/components/ui/label";
import { TagInput } from "@/app/components/ui/tag-input";
import { MlField } from "@/app/components/ui/ml-field";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/app/components/ui/card";
import { FieldHint } from "@/app/components/ui/field-hint";
import { Badge } from "@/app/components/ui/badge";
import { Switch } from "@/app/components/ui/switch";
import { humanSize } from "@/app/utils/format";
import { cn } from "@/app/utils/tw-merge";
import type { LappMeta } from "@/app/pages/upload/extract-lapp-meta";

type MlValue = Partial<Record<Lang, string>>;

export function PackageUploadCard({
  file,
  packageMeta,
  packageError,
  isDragOver,
  fileInputRef,
  setIsDragOver,
  onSelectFile,
  onDrop,
}: {
  file: File | null;
  packageMeta: LappMeta | null;
  packageError: string | null;
  isDragOver: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
  setIsDragOver: (next: boolean) => void;
  onSelectFile: (file: File | null) => Promise<void> | void;
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
}) {
  const { t } = useTranslation();

  return (
    <Card className="overflow-hidden gap-4">
      <CardHeader className="border-b border-border px-5 pt-5 [.border-b]:pb-4">
        <CardTitle className="flex items-center gap-2">
          <HardDriveUpload className="h-4 w-4 text-primary" />
          {t("upload.package.title")}
          <RequiredMark />
        </CardTitle>
        <CardDescription dangerouslySetInnerHTML={{ __html: t("upload.package.sub") }} />
      </CardHeader>
      <CardContent className="space-y-3 px-5 pb-5 pt-4">
        <div
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={onDrop}
          className={cn(
            "cursor-pointer rounded-xl border border-dashed px-5 py-5 transition-colors",
            isDragOver ? "border-primary bg-accent/25" : "border-border bg-muted/20 hover:bg-muted/35"
          )}
        >
          <div className="flex flex-col items-center text-center">
            <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <UploadCloud className="h-5 w-5" />
            </div>
            <p className="text-sm font-semibold" dangerouslySetInnerHTML={{ __html: t("upload.drop.title") }} />
            <p className="mt-1 text-xs text-muted-foreground">{t("upload.drop.sub")}</p>
            <Button type="button" variant="outline" size="sm" className="mt-3">
              {t("upload.package.choose")}
            </Button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".lapp,application/zip"
            hidden
            onChange={(e) => void onSelectFile(e.target.files?.[0] ?? null)}
            required
          />
        </div>

        {file ? (
          <div className="flex items-center gap-3 rounded-xl border border-border bg-card px-3.5 py-2.5">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <FileArchive className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">{file.name}</div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                {humanSize(file.size)}
                {packageMeta?.version ? ` · v${packageMeta.version}` : ""}
              </div>
            </div>
            <Button type="button" size="icon" variant="ghost" onClick={() => void onSelectFile(null)} aria-label="remove">
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <FieldHint>{t("upload.package.empty")}</FieldHint>
        )}

        {packageError && <FieldHint>{packageError}</FieldHint>}
      </CardContent>
    </Card>
  );
}

export function ReleasePolicyCard({
  publish,
  upload,
  force,
  onPublishChange,
  onUploadChange,
  onForceChange,
  file,
  isInspectingPackage,
  packageMeta,
}: {
  publish: boolean;
  upload: boolean;
  force: boolean;
  onPublishChange: (next: boolean) => void;
  onUploadChange: (next: boolean) => void;
  onForceChange: (next: boolean) => void;
  file: File | null;
  isInspectingPackage: boolean;
  packageMeta: LappMeta | null;
}) {
  const { t } = useTranslation();

  return (
    <Card className="gap-4">
      <CardHeader className="border-b border-border px-5 pt-5 [.border-b]:pb-4">
        <CardTitle className="flex items-center gap-2">
          <Settings2 className="h-4 w-4 text-primary" />
          {t("upload.release.title")}
        </CardTitle>
        <CardDescription>{t("upload.release.sub")}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3 px-5 pb-5 pt-4 md:grid-cols-2 xl:grid-cols-3">
        <OptionRow
          title={t("upload.opt.publish")}
          description={t("upload.opt.publishDesc")}
          checked={publish}
          onCheckedChange={onPublishChange}
        />
        <OptionRow
          title={t("upload.opt.storage")}
          description={t("upload.opt.storageDesc")}
          checked={upload}
          onCheckedChange={onUploadChange}
        />
        <OptionRow
          title={t("upload.opt.force")}
          description={t("upload.opt.forceDesc")}
          checked={force}
          onCheckedChange={onForceChange}
        />

        <div className="rounded-xl border border-border bg-muted/20 px-4 py-3 md:col-span-2 xl:col-span-3">
          <div className="mb-2 flex items-center justify-between">
            <Label className="text-xs font-medium text-muted-foreground">{t("upload.release.summary")}</Label>
            <Badge variant={file && !isInspectingPackage ? "primary" : "outline"}>
              {isInspectingPackage
                ? t("upload.release.inspecting")
                : file
                  ? t("upload.release.ready")
                  : t("upload.release.waiting")}
            </Badge>
          </div>
          <div className="grid gap-2 text-xs md:grid-cols-2 xl:grid-cols-4">
            <SummaryCell label={t("upload.release.file")} value={file?.name || "—"} />
            <SummaryCell label={t("upload.release.appId")} value={packageMeta?.appId || "—"} />
            <SummaryCell label={t("upload.release.version")} value={packageMeta?.version || "—"} />
            <SummaryCell
              label={t("upload.release.mode")}
              value={publish ? t("upload.release.modeReleased") : t("upload.release.modeDraft")}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function MetadataCard({
  lang,
  jsonInputRef,
  onImportJson,
  vendor,
  onVendorChange,
  tags,
  onTagsChange,
  descShort,
  onDescShortChange,
  desc,
  onDescChange,
  features,
  onFeaturesChange,
  changelog,
  onChangelogChange,
  depsText,
  onDepsTextChange,
  resultText,
  onReset,
  onSubmit,
  isSubmitting,
  canSubmit,
}: {
  lang: Lang;
  jsonInputRef: React.RefObject<HTMLInputElement>;
  onImportJson: (file: File) => void;
  vendor: string;
  onVendorChange: (value: string) => void;
  tags: string[];
  onTagsChange: (next: string[]) => void;
  descShort: MlValue;
  onDescShortChange: (next: MlValue) => void;
  desc: MlValue;
  onDescChange: (next: MlValue) => void;
  features: MlValue;
  onFeaturesChange: (next: MlValue) => void;
  changelog: MlValue;
  onChangelogChange: (next: MlValue) => void;
  depsText: string;
  onDepsTextChange: (value: string) => void;
  resultText: string | null;
  onReset: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  canSubmit: boolean;
}) {
  const { t } = useTranslation();

  return (
    <Card className="flex-1 gap-4">
      <CardHeader className="border-b border-border px-5 pt-5 [.border-b]:pb-4">
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          {t("upload.metadata.title")}
        </CardTitle>
        <CardDescription>{t("upload.metadata.sub")}</CardDescription>
        <CardAction className="flex items-center gap-2">
          <Badge variant="outline">{lang.toUpperCase()}</Badge>
          <Button variant="soft" size="sm" type="button" onClick={() => jsonInputRef.current?.click()}>
            {t("upload.importJson")}
          </Button>
          <input
            ref={jsonInputRef}
            type="file"
            accept=".json,application/json"
            hidden
            onChange={(e) => {
              const imported = e.target.files?.[0];
              if (imported) onImportJson(imported);
              e.target.value = "";
            }}
          />
        </CardAction>
      </CardHeader>
      <CardContent className="space-y-4 px-5 pb-5 pt-4">
        <div className="grid gap-3 xl:grid-cols-[minmax(0,2fr)_minmax(0,4fr)_minmax(0,4fr)]">
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-medium text-muted-foreground">{t("field.vendor")}</Label>
            <Input value={vendor} onChange={(e) => onVendorChange(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-medium text-muted-foreground">{t("field.tags")}</Label>
            <TagInput value={tags} onChange={onTagsChange} placeholder={t("field.tags.placeholder")} />
          </div>
          <MlField
            base="m_desc_short"
            lang={lang}
            label={t("field.descShort")}
            placeholder={t("field.descShort.placeholder")}
            className="flex flex-col gap-1.5"
            inputClassName="h-9"
            value={descShort}
            onChange={onDescShortChange}
          />
        </div>

        <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
          <MlField
            base="m_desc"
            lang={lang}
            label={t("field.description")}
            placeholder={t("field.description.placeholder")}
            area
            className="flex flex-col gap-1.5"
            textareaClassName="min-h-[96px] resize-y"
            value={desc}
            onChange={onDescChange}
          />
          <MlField
            base="m_features"
            lang={lang}
            label={t("field.features.lines")}
            placeholder={t("field.features.placeholder")}
            area
            className="flex flex-col gap-1.5"
            textareaClassName="min-h-[96px] resize-y"
            value={features}
            onChange={onFeaturesChange}
          />
        </div>

        <div className="grid grid-cols-1 gap-3 xl:grid-cols-3">
          <MlField
            base="m_changelog"
            lang={lang}
            label={t("field.changelog")}
            placeholder={t("field.changelog.placeholder")}
            area
            className="flex flex-col gap-1.5"
            textareaClassName="min-h-[96px] resize-y"
            value={changelog}
            onChange={onChangelogChange}
          />
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-medium text-muted-foreground">{t("field.dependencies.lines")}</Label>
            <Textarea
              value={depsText}
              onChange={(e) => onDepsTextChange(e.target.value)}
              rows={5}
              placeholder={t("field.dependencies.placeholder")}
              className="min-h-[96px] resize-y"
            />
          </div>
          <div className="flex flex-col rounded-xl border border-amber-200 bg-amber-50/90 p-3 xl:self-end">
            <div className="text-sm font-medium text-amber-900">{t("upload.metadata.notes")}</div>
            <div className="mt-2 space-y-1.5">
              <FieldHint className="text-amber-800/90">{t("upload.screenshots.hint")}</FieldHint>
              <FieldHint className="text-amber-800/90">{t("upload.metadata.notesCurate")}</FieldHint>
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter className="justify-between gap-3 border-t border-border px-5 pb-5 pt-4">
        <div className="min-w-0 text-xs text-muted-foreground">
          {resultText ? <span className="truncate">{resultText}</span> : <span>{t("upload.footerHint")}</span>}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button variant="ghost" onClick={onReset} type="button">
            {t("upload.reset")}
          </Button>
          <Button onClick={onSubmit} disabled={isSubmitting || !canSubmit}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isSubmitting ? t("upload.processing") : t("upload.submit")}
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}

function OptionRow({
  title,
  description,
  checked,
  onCheckedChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-xl border border-border bg-muted/15 px-4 py-3">
      <div className="min-w-0">
        <div className="text-sm font-medium">{title}</div>
        <div className="mt-0.5 text-xs leading-5 text-muted-foreground">{description}</div>
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  );
}

function SummaryCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border/70 bg-background/70 px-3 py-2">
      <div className="text-[11px] uppercase tracking-[0.02em] text-muted-foreground">{label}</div>
      <div className="mt-1 truncate text-sm font-medium text-foreground">{value}</div>
    </div>
  );
}
